import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';

export interface SignaturePadRef {
  clear: () => void;
  isEmpty: () => boolean;
  getDataURL: () => string | null;
  loadSignature: (dataURL: string) => void;
}

interface SignaturePadProps {
  label: string;
  onSignatureChange?: (hasSignature: boolean) => void;
}

const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(
  ({ label, onSignatureChange }, ref) => {
    const signatureRef = useRef<SignatureCanvas>(null);

    useImperativeHandle(ref, () => ({
      clear: () => {
        signatureRef.current?.clear();
        onSignatureChange?.(false);
      },
      isEmpty: () => signatureRef.current?.isEmpty() ?? true,
      getDataURL: () => {
        if (signatureRef.current?.isEmpty()) return null;
        return signatureRef.current?.toDataURL('image/png') ?? null;
      },
      loadSignature: (dataURL: string) => {
        signatureRef.current?.fromDataURL(dataURL, { ratio: 1 });
        onSignatureChange?.(true);
      }
    }));

    const handleEnd = () => {
      const hasSignature = !signatureRef.current?.isEmpty();
      onSignatureChange?.(hasSignature);
    };

    const handleClear = () => {
      signatureRef.current?.clear();
      onSignatureChange?.(false);
    };

    return (
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <label className="block text-sm font-medium text-gray-700">{label}</label>
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-red-600 hover:text-red-800"
          >
            Clear
          </button>
        </div>
        <div className="border-2 border-gray-300 rounded-lg bg-white">
          <SignatureCanvas
            ref={signatureRef}
            canvasProps={{
              className: 'w-full h-24 rounded-lg',
              style: { width: '100%', height: '96px' }
            }}
            backgroundColor="white"
            penColor="black"
            onEnd={handleEnd}
          />
        </div>
        <p className="text-xs text-gray-500">Sign above using mouse or touch</p>
      </div>
    );
  }
);

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;

